import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type WorkspaceMode = 'reading' | 'list' | 'canvas'

/** Thin left utility rail visibility. */
export type UtilityRailVisibility = 'hidden' | 'visible'

/**
 * Declarative rail state (Stage 18 / 18B).
 * For Context: `open` means the temporary flyout is available/open — never a permanent wide column.
 * For Intelligence: `open` | `collapsed` | `hidden` as before.
 */
export type RailState = 'hidden' | 'collapsed' | 'open'

export type ShellUtilityItem = {
  id: string
  label: string
  icon: ReactNode
  /** When true, selecting toggles the temporary context flyout. */
  opensFlyout?: boolean
  active?: boolean
  onSelect?: () => void
  disabled?: boolean
}

export interface ShellLayoutConfig {
  /** Optional left contextual panel content (shown in temporary flyout). */
  contextPanel?: ReactNode | null
  /** Optional right detail/inspector panel content. */
  detailPanel?: ReactNode | null
  /** Workspace width mode. */
  workspaceMode?: WorkspaceMode
  /** Compact section label beside the logo (match dock nav labels). */
  workspaceTitle?: string | null
  /** Context flyout header label. */
  contextPanelTitle?: string | null
  /** Intelligence / detail rail header label. */
  detailPanelTitle?: string | null
  /**
   * Context availability. Stage 18B: never mounts a permanent wide left column.
   * `hidden` = no context content. `collapsed` | `open` = content available via utility rail flyout
   * (flyout starts closed; `open` only describes availability / legacy intent).
   */
  contextRail?: RailState
  /** Declarative Intelligence rail state. */
  intelligenceRail?: RailState
  /** Thin left utility rail. Defaults to visible for shell stability. */
  utilityRail?: UtilityRailVisibility
  /** Contextual utility actions. When omitted and contextPanel exists, a Filters action is synthesised. */
  utilityItems?: ShellUtilityItem[]
  /** @deprecated Prefer `contextRail`. */
  contextCollapsedDefault?: boolean
  /** @deprecated Prefer `intelligenceRail`. */
  detailCollapsedDefault?: boolean
  /**
   * Primitive key that must change when panel contents should re-sync.
   * Avoids infinite loops from fresh ReactNode identities each render.
   */
  panelKey?: string | number | boolean | null
}

interface ShellLayoutState extends Required<Pick<ShellLayoutConfig, 'workspaceMode'>> {
  contextPanel: ReactNode | null
  detailPanel: ReactNode | null
  workspaceTitle: string | null
  contextPanelTitle: string
  detailPanelTitle: string
  contextRail: RailState
  intelligenceRail: RailState
  utilityRail: UtilityRailVisibility
  utilityItems: ShellUtilityItem[]
  contextFlyoutOpen: boolean
  setContextFlyoutOpen: (open: boolean) => void
  toggleContextFlyout: () => void
  contextCollapsed: boolean
  detailCollapsed: boolean
  contextOpenMobile: boolean
  detailOpenMobile: boolean
  setContextCollapsed: (collapsed: boolean) => void
  setDetailCollapsed: (collapsed: boolean) => void
  setContextOpenMobile: (open: boolean) => void
  setDetailOpenMobile: (open: boolean) => void
  closeShellOverlays: () => void
  configure: (config: ShellLayoutConfig) => void
  reset: () => void
}

const DEFAULT_STATE = {
  contextPanel: null as ReactNode | null,
  detailPanel: null as ReactNode | null,
  workspaceMode: 'list' as WorkspaceMode,
  workspaceTitle: null as string | null,
  contextPanelTitle: 'Context',
  detailPanelTitle: 'Intelligence',
  contextRail: 'hidden' as RailState,
  intelligenceRail: 'hidden' as RailState,
  utilityRail: 'visible' as UtilityRailVisibility,
  utilityItems: [] as ShellUtilityItem[],
  contextCollapsed: true,
  detailCollapsed: false,
  contextOpenMobile: false,
  detailOpenMobile: false,
}

function resolveRailState(
  explicit: RailState | undefined,
  panel: ReactNode | null | undefined,
  collapsedDefault: boolean | undefined,
): RailState {
  if (explicit) return explicit
  if (panel == null) return 'hidden'
  if (collapsedDefault) return 'collapsed'
  return 'open'
}

const ShellLayoutContext = createContext<ShellLayoutState | null>(null)

export function ShellLayoutProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState({
    contextPanel: DEFAULT_STATE.contextPanel,
    detailPanel: DEFAULT_STATE.detailPanel,
    workspaceMode: DEFAULT_STATE.workspaceMode,
    workspaceTitle: DEFAULT_STATE.workspaceTitle,
    contextPanelTitle: DEFAULT_STATE.contextPanelTitle,
    detailPanelTitle: DEFAULT_STATE.detailPanelTitle,
    contextRail: DEFAULT_STATE.contextRail,
    intelligenceRail: DEFAULT_STATE.intelligenceRail,
    utilityRail: DEFAULT_STATE.utilityRail,
    utilityItems: DEFAULT_STATE.utilityItems,
  })
  const [contextFlyoutOpen, setContextFlyoutOpen] = useState(false)
  const [detailCollapsed, setDetailCollapsed] = useState(false)
  const [detailOpenMobile, setDetailOpenMobile] = useState(false)

  const closeShellOverlays = useCallback(() => {
    setContextFlyoutOpen(false)
    setDetailOpenMobile(false)
  }, [])

  const configure = useCallback((next: ShellLayoutConfig) => {
    const contextRail = resolveRailState(
      next.contextRail,
      next.contextPanel,
      next.contextCollapsedDefault,
    )
    const intelligenceRail = resolveRailState(
      next.intelligenceRail,
      next.detailPanel,
      next.detailCollapsedDefault,
    )
    const nextContextPanel = next.contextPanel ?? null
    setConfig({
      contextPanel: nextContextPanel,
      detailPanel: next.detailPanel ?? null,
      workspaceMode: next.workspaceMode ?? 'list',
      workspaceTitle: next.workspaceTitle ?? null,
      contextPanelTitle: next.contextPanelTitle ?? DEFAULT_STATE.contextPanelTitle,
      detailPanelTitle: next.detailPanelTitle ?? DEFAULT_STATE.detailPanelTitle,
      contextRail,
      intelligenceRail,
      utilityRail: next.utilityRail ?? 'visible',
      utilityItems: next.utilityItems ?? [],
    })
    setDetailCollapsed(intelligenceRail === 'collapsed')
    // Do not close the flyout on every panelKey refresh (filter edits).
    // Route changes / reset / missing context content close it instead.
    if (nextContextPanel == null || contextRail === 'hidden') {
      setContextFlyoutOpen(false)
    }
  }, [])

  const reset = useCallback(() => {
    setConfig({
      contextPanel: DEFAULT_STATE.contextPanel,
      detailPanel: DEFAULT_STATE.detailPanel,
      workspaceMode: DEFAULT_STATE.workspaceMode,
      workspaceTitle: DEFAULT_STATE.workspaceTitle,
      contextPanelTitle: DEFAULT_STATE.contextPanelTitle,
      detailPanelTitle: DEFAULT_STATE.detailPanelTitle,
      contextRail: DEFAULT_STATE.contextRail,
      intelligenceRail: DEFAULT_STATE.intelligenceRail,
      utilityRail: DEFAULT_STATE.utilityRail,
      utilityItems: DEFAULT_STATE.utilityItems,
    })
    setContextFlyoutOpen(false)
    setDetailCollapsed(false)
    setDetailOpenMobile(false)
  }, [])

  const toggleContextFlyout = useCallback(() => {
    setContextFlyoutOpen((open) => !open)
  }, [])

  const setContextCollapsed = useCallback((collapsed: boolean) => {
    setContextFlyoutOpen(!collapsed)
  }, [])

  const setContextOpenMobile = useCallback((open: boolean) => {
    setContextFlyoutOpen(open)
  }, [])

  const effectiveIntelligenceRail: RailState =
    config.intelligenceRail === 'hidden'
      ? 'hidden'
      : detailCollapsed
        ? 'collapsed'
        : 'open'

  // Context effective state: hidden | collapsed (available, flyout closed) | open (flyout open)
  const effectiveContextRail: RailState =
    config.contextRail === 'hidden' || config.contextPanel == null
      ? 'hidden'
      : contextFlyoutOpen
        ? 'open'
        : 'collapsed'

  const value = useMemo<ShellLayoutState>(
    () => ({
      ...config,
      contextRail: effectiveContextRail,
      intelligenceRail: effectiveIntelligenceRail,
      contextFlyoutOpen,
      setContextFlyoutOpen,
      toggleContextFlyout,
      contextCollapsed: !contextFlyoutOpen,
      detailCollapsed,
      contextOpenMobile: contextFlyoutOpen,
      detailOpenMobile,
      setContextCollapsed,
      setDetailCollapsed,
      setContextOpenMobile,
      setDetailOpenMobile,
      closeShellOverlays,
      configure,
      reset,
    }),
    [
      config,
      effectiveContextRail,
      effectiveIntelligenceRail,
      contextFlyoutOpen,
      toggleContextFlyout,
      detailCollapsed,
      detailOpenMobile,
      setContextCollapsed,
      setContextOpenMobile,
      closeShellOverlays,
      configure,
      reset,
    ],
  )

  return <ShellLayoutContext.Provider value={value}>{children}</ShellLayoutContext.Provider>
}

export function useShellLayout(): ShellLayoutState {
  const ctx = useContext(ShellLayoutContext)
  if (!ctx) {
    throw new Error('useShellLayout must be used within ShellLayoutProvider')
  }
  return ctx
}

/**
 * Declare shell regions for the current route. Clears on unmount.
 * Pass `panelKey` whenever panel contents should refresh (e.g. filter or brief id).
 */
export function useShellConfiguration(config: ShellLayoutConfig): void {
  const { configure, reset } = useShellLayout()
  const panelPresence = `${config.contextPanel != null}:${config.detailPanel != null}`
  const panelKey = config.panelKey ?? panelPresence
  const utilityKey = config.utilityItems?.map((i) => `${i.id}:${i.active ? 1 : 0}`).join('|') ?? ''

  useEffect(() => {
    configure(config)
    return () => reset()
    // Intentionally omit ReactNode identities — use panelKey for content sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stable shell sync contract
  }, [
    configure,
    reset,
    config.workspaceMode,
    config.workspaceTitle,
    config.contextPanelTitle,
    config.detailPanelTitle,
    config.contextRail,
    config.intelligenceRail,
    config.utilityRail,
    config.contextCollapsedDefault,
    config.detailCollapsedDefault,
    panelKey,
    utilityKey,
  ])
}
